<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import moment from 'moment';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiChoiceCards from '@/components/ui/UiChoiceCards.vue';
import UiDetailList from '@/components/ui/UiDetailList.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormFields from '@/components/ui/UiFormFields.vue';
import UiSteps from '@/components/ui/UiSteps.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import {
  ACADEMIC_REASONS,
  ATTACHMENT_ACCEPT,
  MONITORING_STATUSES,
  OFFENSE_CATEGORIES,
  PREVIOUS_INTERVENTIONS,
  TEACHER_ACTIONS,
  attachmentProblem,
  attachmentRule,
  fileOf,
} from '@/constants/reports';
import { useToast } from '@/composables/useToast';
import { formatDate, formatTime, titleCase } from '@/utils/format';

// Files an academic or disciplinary concern about a student in one of the
// teacher's classes. It goes to the class adviser first (For resolution):
// only the adviser resolves it or endorses it to the Prefect of Discipline,
// with the reason; the reporting teacher follows it. When the teacher is
// the class's adviser, it goes straight to the Prefect.
// A disciplinary concern describes the incident: the kind of offense, when
// and where, what happened, what the teacher did on the spot, witnesses and
// other students involved (from any class), and optional evidence. An
// academic concern is a follow-up on a student the system flags At-Risk or
// LARDO (only those are listed): their monitoring status, why, what was
// already tried, and what the teacher recommends.
// With mode="prefect" the Prefect of Discipline records a disciplinary case
// no teacher reported, about any student at the grades they handle; it
// starts with them, and the class adviser is told.
// Not every teacher is at ease with long forms, so it goes one step at a
// time: the kind of concern (big cards that say what each is for), the
// student, the details, then a review of everything before it's sent.
const props = defineProps({
  // Row from Class records: roomId, subjectId, subject_title, grade_level, room_section.
  classInfo: { type: Object, default: null },
  mode: { type: String, default: 'teacher', validator: (v) => ['teacher', 'prefect'].includes(v) },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const store = useStore();
const { busy, run } = useAsyncAction();
const toast = useToast();

const students = shallowRef([]);
// Everyone enrolled this school year, for "other students involved".
const directory = shallowRef([]);
const form = ref({});
const today = () => moment().format('YYYY-MM-DD');

const TYPE_CHOICES = [
  {
    id: 1,
    icon: 'mdi-school-outline',
    title: 'Academic concern',
    description: 'A follow-up on a student who is At-Risk or LARDO and still struggling after your help.',
  },
  {
    id: 2,
    icon: 'mdi-alert-outline',
    title: 'Disciplinary concern',
    description: 'Something the student did: misbehavior, cheating, bullying, a fight, and the like.',
  },
];
const prefect = computed(() => props.mode === 'prefect');
const academic = computed(() => !prefect.value && form.value.report_type === 1);

// "Dela Cruz, Juan M. — Grade 8 · SAMPAGUITA · LRN 1234…"
function directoryItem(s) {
  const middle = String(s.mname ?? '').trim();
  const name = titleCase([s.lname && `${s.lname},`, s.fname, middle && middle.toLowerCase() !== 'n/a' ? `${middle[0]}.` : ''].filter(Boolean).join(' '));
  return {
    value: s.id,
    title: `${name} — ${[s.grade_level, s.section].filter(Boolean).join(' · ') || 'No section'}${s.lrnNo ? ` · LRN ${s.lrnNo}` : ''}`,
  };
}

// What a teacher can recommend on an academic concern: the Prefect's
// disciplinary actions in use, so a renamed or archived one shows as such.
const recommendedActions = shallowRef([]);
async function loadRecommendedActions() {
  const list = await apiRequest('get', '/parent-records/disciplinary-actions').catch(() => []);
  recommendedActions.value = (Array.isArray(list) ? list : []).map((a) => ({ value: a.id, title: a.name }));
}

// The students of this class an academic concern can be about: those the
// system flags At-Risk or LARDO, each with that status.
const flagged = shallowRef([]);
const loadingFlagged = shallowRef(false);
async function loadFlagged() {
  loadingFlagged.value = true;
  try {
    const list = await apiRequest('get', `/parent-records/monitoring-students/${store.getters.getFilterSelected}`, undefined, {
      params: { roomID: props.classInfo?.roomId, subjectID: props.classInfo?.subjectId },
    });
    flagged.value = (Array.isArray(list) ? list : []).map((s) => {
      const middle = String(s.mname ?? '').trim();
      const name = titleCase([s.fname, middle && middle.toLowerCase() !== 'n/a' ? `${middle[0]}.` : '', s.lname].filter(Boolean).join(' '));
      return {
        value: s.id,
        title: `${name}${s.lrnNo ? ` · ${s.lrnNo}` : ''} — ${MONITORING_STATUSES[s.monitoring_status]?.name ?? ''}`,
      };
    });
  } catch {
    flagged.value = [];
  } finally {
    loadingFlagged.value = false;
  }
}

watch(open, async (isOpen) => {
  if (!isOpen || (!props.classInfo && !prefect.value)) return;
  step.value = 0;
  form.value = {
    studentID: null,
    // The teacher picks it on the first step; the Prefect only records
    // disciplinary cases.
    report_type: prefect.value ? 2 : null,
    report_date: today(),
    report_time: moment().format('HH:mm'),
    report_description: '',
    tag_students: [],
    offense_category: null,
    incident_location: '',
    witnesses: '',
    teacher_action: null,
    parent_informed: false,
    evidence: null,
    academic_reason: null,
    previous_intervention: null,
    recommended_action_id: null,
  };
  monitoring.value = null;
  students.value = [];
  flagged.value = [];
  const sy = store.getters.getFilterSelected;
  if (prefect.value) {
    // Any student at the Prefect's grades.
    const mine = await apiRequest('get', `/parent-records/students-directory/${sy}`, undefined, { params: { mine: true } }).catch(() => []);
    students.value = (Array.isArray(mine) ? mine : []).map(directoryItem);
  } else {
    const list = await apiRequest(
      'get',
      `/rooms-section/getMyStudentAttendance/${store.state.user.id}/${sy}/${props.classInfo.roomId}`,
    ).catch(() => []);
    students.value = (Array.isArray(list) ? list : []).map((s) => ({
      value: s.id,
      title: titleCase(s.name) + (s.lrnNo ? ` · ${s.lrnNo}` : ''),
    }));
    loadFlagged();
    loadRecommendedActions();
  }
  const all = await apiRequest('get', `/parent-records/students-directory/${sy}`).catch(() => []);
  directory.value = (Array.isArray(all) ? all : []).map(directoryItem);
});

// Switching to an academic concern keeps the student only if they're
// flagged.
watch(academic, (isAcademic) => {
  if (isAcademic && form.value.studentID && !flagged.value.some((s) => s.value === form.value.studentID)) {
    form.value = { ...form.value, studentID: null };
  }
});

// The student being reported can't also be tagged as involved.
const others = computed(() => directory.value.filter((s) => s.value !== form.value.studentID));
watch(
  () => form.value.studentID,
  (id) => {
    if (form.value.tag_students?.includes(id)) {
      form.value = { ...form.value, tag_students: form.value.tag_students.filter((t) => t !== id) };
    }
  },
);

// The chosen student's monitoring status, for an academic concern. The
// server works it out again when the report is filed; this only shows it.
const monitoring = shallowRef(null);
const loadingMonitoring = shallowRef(false);
watch(
  () => (open.value && academic.value ? form.value.studentID : null),
  async (id) => {
    monitoring.value = null;
    if (!id) return;
    loadingMonitoring.value = true;
    try {
      const status = await apiRequest(
        'get',
        `/parent-records/monitoring-status/${store.getters.getFilterSelected}/${id}?subjectID=${props.classInfo?.subjectId ?? ''}`,
      );
      if (form.value.studentID === id) monitoring.value = status;
    } catch {
      monitoring.value = null;
    } finally {
      loadingMonitoring.value = false;
    }
  },
);
const monitoringBadge = computed(() => MONITORING_STATUSES[monitoring.value?.status] ?? null);

const notFuture = (v) => !v || v <= today() || 'The date cannot be in the future.';

// --- Steps ------------------------------------------------------------------
// The Prefect skips the first step (their cases are always disciplinary).
const STEP_NAMES = { type: 'Kind of concern', student: 'Student', details: 'Details', review: 'Review' };
const stepKeys = computed(() => (prefect.value ? ['student', 'details', 'review'] : ['type', 'student', 'details', 'review']));
const step = shallowRef(0);
const stepKey = computed(() => stepKeys.value[step.value]);
const stepLabels = computed(() => stepKeys.value.map((k) => STEP_NAMES[k]));
const isLast = computed(() => step.value === stepKeys.value.length - 1);
const formRef = ref(null);

function chooseType(choice) {
  form.value = { ...form.value, report_type: choice.id };
  step.value += 1;
}
// Only the fields on screen are checked before moving on.
async function next() {
  if (stepKey.value === 'type' && !form.value.report_type) {
    toast.error('Choose the kind of concern first.');
    return;
  }
  const { valid } = (await formRef.value?.validate()) ?? { valid: true };
  if (!valid) return;
  if (stepKey.value === 'details' && !academic.value) {
    const problem = attachmentProblem(fileOf(form.value.evidence));
    if (problem) {
      toast.error(problem);
      return;
    }
  }
  step.value += 1;
}
const back = () => (step.value = Math.max(0, step.value - 1));
const goTo = (key) => (step.value = Math.max(0, stepKeys.value.indexOf(key)));

// What each step asks, in plain words.
const stepHint = computed(
  () =>
    ({
      type: 'What kind of concern are you reporting?',
      student: academic.value
        ? 'Which student is this about? Only students flagged At-Risk or LARDO can have an academic concern.'
        : 'Which student is this about?',
      details: academic.value
        ? 'Tell the adviser why you are following up and what you have tried.'
        : 'Tell what happened. Fields marked * are required; the rest are optional.',
      review: 'Check your answers. Click Edit to change anything, then submit.',
    })[stepKey.value],
);

const studentFields = computed(() => [
  {
    key: 'studentID',
    label: 'Student',
    type: 'autocomplete',
    required: true,
    items: academic.value ? flagged.value : students.value,
    span: 2,
    placeholder: 'Search by name or LRN',
    description: academic.value
      ? loadingFlagged.value
        ? 'Loading At-Risk and LARDO students…'
        : flagged.value.length
          ? 'Only students flagged At-Risk or LARDO are listed.'
          : 'No At-Risk or LARDO students in this class.'
      : prefect.value
        ? 'Any student at the grades you handle.'
        : undefined,
  },
]);
const academicFields = computed(() => [
  { key: 'academic_reason', label: '1. Reason for academic follow-up', type: 'select', required: true, items: ACADEMIC_REASONS, span: 2 },
  { key: 'previous_intervention', label: '2. Previous intervention provided', type: 'select', required: true, items: PREVIOUS_INTERVENTIONS, span: 2 },
  {
    key: 'report_description',
    label: '3. Description / progress observation',
    type: 'textarea',
    required: true,
    span: 2,
    placeholder: 'What you have seen since the intervention: scores, missing work, participation.',
  },
  {
    key: 'recommended_action_id',
    label: '4. Recommended action',
    type: 'select',
    required: true,
    items: recommendedActions.value,
    span: 2,
  },
]);
// The required details first, then the optional ones under their own heading.
const disciplinaryFields = computed(() => [
  { key: 'offense_category', label: 'Kind of offense', type: 'select', required: true, items: OFFENSE_CATEGORIES, span: 2 },
  { key: 'report_date', label: 'Date of incident', type: 'date', required: true, max: today(), rules: [notFuture] },
  { key: 'report_time', label: 'Time of incident', type: 'time', required: true },
  {
    key: 'incident_location',
    label: 'Where it happened',
    type: 'text',
    required: true,
    span: 2,
    placeholder: 'e.g. Classroom, canteen, hallway, school grounds',
    rules: [(v) => String(v ?? '').length <= 100 || 'Keep it to 100 characters.'],
  },
  {
    key: 'report_description',
    label: 'What happened',
    type: 'textarea',
    required: true,
    span: 2,
    placeholder: 'Describe the incident: what happened, where, and who was there.',
  },
  {
    key: 'teacher_action',
    label: prefect.value ? 'Action taken on the spot' : 'Action you took',
    type: 'select',
    required: true,
    items: TEACHER_ACTIONS,
    span: 2,
  },
]);
const optionalFields = computed(() => [
  {
    key: 'tag_students',
    label: 'Other students involved',
    type: 'autocomplete',
    multiple: true,
    items: others.value,
    span: 2,
    description: 'Optional. Any student in the school; search by name or LRN. Each shows their grade, section and LRN.',
    placeholder: 'Search students',
  },
  {
    key: 'witnesses',
    label: 'Witnesses',
    type: 'text',
    span: 2,
    description: 'Optional. Teachers, staff or students who saw it.',
    rules: [(v) => String(v ?? '').length <= 255 || 'Keep it to 255 characters.'],
  },
  {
    key: 'evidence',
    label: 'Evidence',
    type: 'file',
    accept: ATTACHMENT_ACCEPT,
    rules: [attachmentRule],
    span: 2,
    description: 'Optional. A photo, screenshot or written statement: a PDF, JPG or PNG of up to 1MB. Seen by staff only.',
  },
]);

// --- Review -------------------------------------------------------------------
const titleIn = (list, value) => list.find((o) => o.value === value)?.title ?? '—';
const studentName = computed(
  () => (academic.value ? flagged.value : students.value).find((s) => s.value === form.value.studentID)?.title ?? '—',
);
const review = computed(() => {
  const f = form.value;
  const sections = [
    ...(prefect.value
      ? []
      : [{ key: 'type', title: 'Kind of concern', items: [{ label: 'Concern', value: TYPE_CHOICES.find((t) => t.id === f.report_type)?.title, span: 2 }] }]),
    {
      key: 'student',
      title: 'Student',
      items: [
        { label: 'Student', value: studentName.value, span: 2 },
        ...(academic.value && monitoringBadge.value
          ? [{ label: 'Monitoring status', value: [monitoringBadge.value.name, monitoring.value?.detail].filter(Boolean).join(' · '), span: 2 }]
          : []),
      ],
    },
  ];
  const details = academic.value
    ? [
        { label: 'Reason for follow-up', value: titleIn(ACADEMIC_REASONS, f.academic_reason), span: 2 },
        { label: 'Previous intervention', value: titleIn(PREVIOUS_INTERVENTIONS, f.previous_intervention), span: 2 },
        { label: 'Progress observation', value: f.report_description, span: 2 },
        { label: 'Recommended action', value: titleIn(recommendedActions.value, f.recommended_action_id), span: 2 },
      ]
    : [
        { label: 'Kind of offense', value: titleIn(OFFENSE_CATEGORIES, f.offense_category), span: 2 },
        { label: 'Date', value: formatDate(f.report_date) },
        { label: 'Time', value: formatTime(f.report_time) },
        { label: 'Where', value: f.incident_location, span: 2 },
        { label: 'What happened', value: f.report_description, span: 2 },
        { label: prefect.value ? 'Action taken on the spot' : 'Action you took', value: titleIn(TEACHER_ACTIONS, f.teacher_action), span: 2 },
        {
          label: 'Other students involved',
          value: (f.tag_students || []).map((id) => directory.value.find((d) => d.value === id)?.title).filter(Boolean).join('\n') || 'None',
          span: 2,
        },
        { label: 'Witnesses', value: f.witnesses || 'None named' },
        { label: 'Parents already told', value: f.parent_informed ? 'Yes' : 'No' },
        { label: 'Evidence', value: fileOf(f.evidence)?.name || 'None attached', span: 2 },
      ];
  return [...sections, { key: 'details', title: 'Details', items: details }];
});

const description = computed(() =>
  prefect.value
    ? 'Record a case no teacher reported. It starts with you, and the class adviser is told.'
    : [titleCase(props.classInfo?.subject_title), props.classInfo?.grade_level, props.classInfo?.room_section]
        .filter(Boolean)
        .join(' · '),
);

async function submit() {
  const evidence = academic.value ? null : fileOf(form.value.evidence);
  const problem = attachmentProblem(evidence);
  if (problem) {
    toast.error(problem);
    return;
  }
  const c = props.classInfo ?? {};
  const body = {
    studentID: form.value.studentID,
    school_yearID: store.getters.getFilterSelected,
    // The Prefect's case takes the student's section and grade on the server.
    ...(prefect.value ? {} : { subjectID: c.subjectId, roomID: c.roomId, grade_level: c.grade_level }),
    report_type: prefect.value ? 2 : form.value.report_type,
    // An academic follow-up has no incident: it's dated when filed.
    report_date: academic.value ? today() : form.value.report_date,
    report_time: academic.value ? moment().format('HH:mm') : form.value.report_time,
    report_description: (form.value.report_description ?? '').trim(),
    tag_students: JSON.stringify(academic.value ? [] : form.value.tag_students || []),
    ...(academic.value
      ? {
          academic_reason: form.value.academic_reason,
          previous_intervention: form.value.previous_intervention,
          recommended_action_id: form.value.recommended_action_id,
        }
      : {
          offense_category: form.value.offense_category,
          incident_location: (form.value.incident_location ?? '').trim(),
          witnesses: (form.value.witnesses ?? '').trim(),
          teacher_action: form.value.teacher_action,
          parent_informed: !!form.value.parent_informed,
        }),
  };
  // With evidence it goes as a multipart form, so every value travels as text.
  let payload = body;
  if (evidence) {
    payload = new FormData();
    Object.entries(body).forEach(([k, v]) => payload.append(k, String(v ?? '')));
    payload.append('file', evidence);
  }
  let saved = null;
  const ok = await run(
    async () =>
      (saved = await apiRequest('post', prefect.value ? '/parent-records/reports/prefect' : '/parent-records/studentReport', payload)),
    { error: prefect.value ? 'Could not record the case.' : 'Could not file the report.' },
  );
  if (ok) {
    // A report about the teacher's own advisory class skips the adviser.
    toast.success(
      prefect.value
        ? 'Case recorded. The class adviser has been told.'
        : saved?.toPrefect
          ? 'Report filed. You advise this class, so it went straight to the Prefect of Discipline.'
          : "Report filed. It's now for resolution with the class adviser.",
    );
    open.value = false;
    step.value = 0;
    // The new case's id, so the Prefect can go on to it.
    emit('saved', saved?.id ?? null);
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="prefect ? 'Add record' : 'File a report'"
    :description="description"
    size="lg"
  >
    <div class="wizard">
      <UiSteps :steps="stepLabels" :current="step" class="wizard__steps" />
      <p class="wizard__hint">
        <span class="wizard__count">Step {{ step + 1 }} of {{ stepKeys.length }}</span>
        {{ stepHint }}
      </p>

      <!-- 1. Kind of concern: one click picks it and moves on -->
      <template v-if="stepKey === 'type'">
        <UiChoiceCards :items="TYPE_CHOICES" @select="chooseType" />
        <p v-if="form.report_type" class="wizard__chosen">
          Chosen: <strong>{{ TYPE_CHOICES.find((t) => t.id === form.report_type)?.title }}</strong>. Click a card to change it.
        </p>
      </template>

      <v-form v-else-if="stepKey !== 'review'" ref="formRef" class="form-grid" @submit.prevent="next">
        <!-- 2. Student -->
        <template v-if="stepKey === 'student'">
          <UiFormFields v-model:values="form" :fields="studentFields" />
          <section v-if="academic && form.studentID" class="monitoring span-2" aria-label="Student monitoring status">
            <div class="monitoring__head">
              <span class="monitoring__title">Student monitoring status</span>
              <UiBadge v-if="monitoringBadge" :variant="monitoringBadge.variant">{{ monitoringBadge.name }}</UiBadge>
            </div>
            <p class="monitoring__text">
              {{
                loadingMonitoring
                  ? 'Checking…'
                  : monitoring?.detail || (monitoring ? 'No attendance or grade alerts this school year.' : 'Could not load the status.')
              }}
            </p>
            <p class="monitoring__hint">Set by the system from attendance and grades. You don't need to change anything.</p>
          </section>
        </template>

        <!-- 3. Details -->
        <template v-else-if="stepKey === 'details'">
          <UiFormFields v-if="academic" v-model:values="form" :fields="academicFields" />
          <template v-else>
            <UiFormFields v-model:values="form" :fields="disciplinaryFields" />
            <p class="wizard__section span-2">Optional details</p>
            <UiFormFields v-model:values="form" :fields="optionalFields" />
            <v-switch
              :model-value="form.parent_informed"
              class="span-2"
              color="primary"
              :label="prefect ? 'The parents have already been told' : 'I have already told the parents'"
              inset
              density="compact"
              hide-details
              @update:model-value="form = { ...form, parent_informed: !!$event }"
            />
          </template>
        </template>
      </v-form>

      <!-- 4. Review -->
      <template v-else>
        <section v-for="part in review" :key="part.key" class="review-part" :aria-label="part.title">
          <div class="review-part__head">
            <span class="review-part__title">{{ part.title }}</span>
            <v-btn variant="text" size="small" prepend-icon="mdi-pencil-outline" @click="goTo(part.key)">Edit</v-btn>
          </div>
          <UiDetailList :items="part.items" />
        </section>
        <p class="recipient">
          <v-icon size="16">mdi-send-outline</v-icon>
          {{
            prefect
              ? 'The case starts with you. The class adviser will be told.'
              : academic
                ? 'Goes to the class adviser, who reviews it and decides whether to endorse it for further intervention.'
                : 'Goes to the class adviser, who resolves it or endorses it to the Prefect of Discipline.'
          }}
        </p>
      </template>
    </div>

    <template #footer="{ close }">
      <v-btn v-if="step === 0" variant="outlined" @click="close">Cancel</v-btn>
      <v-btn v-else variant="outlined" prepend-icon="mdi-arrow-left" :disabled="busy" @click="back">Back</v-btn>
      <v-btn v-if="isLast" color="primary" prepend-icon="mdi-send-outline" :loading="busy" @click="submit">
        {{ prefect ? 'Record case' : 'Submit report' }}
      </v-btn>
      <v-btn v-else-if="stepKey !== 'type'" color="primary" append-icon="mdi-arrow-right" @click="next">Next</v-btn>
      <v-btn v-else-if="form.report_type" color="primary" append-icon="mdi-arrow-right" @click="next">Next</v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.monitoring {
  display: grid;
  gap: 2px;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--subtle);
}
.monitoring__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.monitoring__title {
  font-size: 14px;
  font-weight: 600;
}
.monitoring__text {
  margin: 0;
  font-size: 14px;
}
.wizard {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.wizard__steps {
  margin-bottom: 4px;
}
.wizard__hint {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  font-size: 15px;
  font-weight: 500;
}
.wizard__count {
  font-size: 12px;
  font-weight: 500;
  color: var(--muted-foreground);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.wizard__chosen {
  margin: 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
.wizard__section {
  margin: 8px 0 0;
  padding-top: 12px;
  border-top: 1px solid var(--border);
  font-size: 14px;
  font-weight: 600;
}
.review-part {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.review-part__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.review-part__title {
  font-size: 14px;
  font-weight: 600;
}
.monitoring__hint,
.recipient {
  margin: 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
.recipient {
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
